// Everything one row of the files list needs, shared by the desktop row
// (file-row.tsx) and the phone card (file-card.tsx): what to show (from the
// pure rules in file-row-model.ts and file-row-menu-rules.ts) and what each
// button does (the modals below). The two components only lay it out.
import { useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAppDispatch } from "hooks";
import { setModalxComponent, setModalxComponentProps } from "components/modalx/modalx.slice";
import { openClickSign, openPdfPreview } from "components/modalx/modalx-openers";
import { API_URL } from "../../constants";
import {
  extractSignersFromList,
  getNonDisabledUserIds,
  getSignerId,
  isSignatureLockedFile,
} from "utils/signatureUtils";
import { createHandleRepeatEmail, openDokobitSignUrl } from "utils/signatureViewUtils";
import { useUploadSignedCopy } from "features/replace-file/replace-file";
import type { FileRowMenuItem } from "./file-row-menu";
import type { FileItem } from "./file-item";
import {
  type RowAction,
  type RowSigner,
  type StatusPill,
  canViewFile,
  getAvailableActions,
  getCategoryTitle,
  getMenuActions,
  getRequestUserSigners,
  getRowNumber,
  getRowSigners,
  getStatusPill,
  isSelectableRow,
  isWithoutSigning,
  pickPrimaryAction,
  templateIdOf,
} from "./file-row-model";
import {
  getEditMode,
  getFileViewUrl,
  getRowCategoryId,
  isEditRequestLocked,
  isModuleManagedRow,
} from "./file-row-menu-rules";

/** What the screen rendering <Files> lets its rows do, plus list-level handlers. */
export interface FilesListContext {
  companyId: string | undefined;
  // The customer in the page URL (customer card, customer's order page).
  routeCustomerId: string | undefined;
  currentUserId: string | undefined;
  canOpenDocumentPage: boolean;
  customerId: string;
  parentFileCustomerId: string;
  parentFileCustomers: { id: string; name?: string }[];
  parentTemplateId: string;
  parentCategoryId: string;
  isEditable: boolean;
  subject: string;
  source: string;
  orderEntity: { id: string } | null;
  isOrderAmendment: boolean;
  allowParentActions: boolean;
  selectedFileIds: Record<string, boolean>;
  onToggleFileSelection?: (item: FileItem) => void;
  onChanged?: () => void;
  openMoveModal: (item: FileItem) => void;
  openMakeSeparateModal: (item: FileItem) => void;
}

const noop = () => {};

export interface FileRowView {
  title: string;
  // Set only when the title may link to the document page.
  documentPageUrl: string | null;
  status: StatusPill | null;
  // No pill, no sign type and no signers: the phone card says "Be pasirašymo".
  isWithoutSigning: boolean;
  signers: RowSigner[];
  number: string;
  categoryTitle: string;
  related: FileItem[];
  isSelectable: boolean;
  isSelected: boolean;
  toggleSelected: () => void;
  primary: { label: string; run: () => void } | null;
  menuItems: FileRowMenuItem[];
  canView: boolean;
  view: () => void;
  downloadUrl: string | null;
  remind: (signer: RowSigner) => void;
  remindingKey: string | null;
  // The hidden file picker behind "Įkelti pasirašytą dokumentą", set only when
  // the row offers it: render <UploadSignedCopyInput> with these once.
  signedCopyUpload: {
    inputRef: RefObject<HTMLInputElement>;
    onFile: (file: File) => void;
  } | null;
}

export default function useFileRow(
  item: FileItem,
  ctx: FilesListContext,
  isChild: boolean
): FileRowView {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { companyId, onChanged } = ctx;
  const [remindingKey, setRemindingKey] = useState<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const uploadSignedCopy = useUploadSignedCopy({
    parentFileId: item.id,
    onChange: onChanged || noop,
  });

  const signers = getRowSigners(item, ctx);
  const actions = getAvailableActions(item, ctx, signers);
  const primary = pickPrimaryAction(actions);
  const editMode = getEditMode(item, ctx);
  const fileViewUrl = getFileViewUrl(item, companyId, ctx.parentCategoryId);
  const documentPageUrl = ctx.canOpenDocumentPage ? fileViewUrl : null;

  const openModal = (component: string, props: Record<string, unknown>) => {
    dispatch(setModalxComponent(component));
    dispatch(setModalxComponentProps({ ...props, onSuccess: onChanged }));
  };

  // The modal behind "Siųsti / Redaguoti pasirašymo užklausą" on a child row
  // of a document's page; offers the row's (or the parent's) customers.
  const openMultiSignerModal = (userSigners: unknown[], contactSigners: unknown[]) => {
    const customers =
      Array.isArray(item.customer) && item.customer.length > 0
        ? item.customer
        : ctx.parentFileCustomers;
    openModal("FileSignMultiUserAssignPopup", {
      fileId: item.id,
      companyId,
      usersList: getNonDisabledUserIds(userSigners),
      customerId: item.customer?.[0]?.id || ctx.parentFileCustomerId,
      customerIds: customers.map((customer) => customer.id),
      customers,
      contactSigners,
      parentFileId: item.relatedTo,
      signType: item.signType,
      tabUser: item.signontab?.tabUser,
      categoryId: getRowCategoryId(item, ctx.parentCategoryId),
      // A signer already on a locked (template-built) document may only be
      // added to, never removed, in that modal.
      isTemplateGenerated: isSignatureLockedFile(item) || ctx.isOrderAmendment,
    });
  };

  const runAction = (action: RowAction) => {
    const { permissionUserSigner, userSigner, contactSigners } =
      extractSignersFromList(item.signersList);
    switch (action) {
      case "sign_dokobit":
        if (companyId) openDokobitSignUrl(companyId, item.id);
        return;
      case "sign_click":
        openClickSign(dispatch, item.id, onChanged);
        return;
      case "sign_request_upload":
        openModal("FileSignAssignPopup", {
          fileId: item.id,
          companyId,
          customerId: ctx.customerId,
          relatedTo: item.relatedTo,
        });
        return;
      case "sign_request_two_step":
        openModal("FileSignUserAssignPopup", {
          fileId: item.id,
          companyId,
          customerId: ctx.customerId,
          relatedTo: item.relatedTo,
          permissionUserSigner,
          userSigner,
        });
        return;
      case "sign_request_one_step":
        openModal("SignRequestConfirmPopup", {
          fileId: item.id,
          contactSigners: contactSigners.map(getSignerId).filter(Boolean),
          companyUserSigner: getSignerId(permissionUserSigner),
          customerId: ctx.customerId,
          nontechuser: getSignerId(userSigner),
          signType: item.signType,
        });
        return;
      case "send_signature_request":
        openMultiSignerModal([], []);
        return;
      case "edit_sign_request":
        openMultiSignerModal(
          getRequestUserSigners(item),
          item.dokobit?.contactSigners || item.signontab?.contactSigners || []
        );
        return;
      case "send_email":
        openModal("ManualSignSendMailConfirm", {
          customerId: ctx.customerId,
          fileId: item.id,
          companyId,
        });
        return;
      case "upload_signed":
        uploadInputRef.current?.click();
        return;
    }
  };

  const edit = () => {
    if (editMode.disabled) return;
    if (editMode.mode === "file") {
      openModal("FileForm", {
        fileId: item.id,
        companyId,
        isUserSelectorVisible: ctx.subject === "user",
      });
      return;
    }
    openModal("OrderDocumentForm", {
      customerId: ctx.customerId || ctx.routeCustomerId,
      template: templateIdOf(item) || ctx.parentTemplateId,
      fileId: item.relatedTo,
      assignedUserId: "",
      id: item.id,
      subject: "customer",
      ...(ctx.orderEntity && { orderEntity: ctx.orderEntity, orderId: ctx.orderEntity.id }),
      ...(ctx.isOrderAmendment && {
        isOrderAmendment: true,
        // A system-built amendment has no template of its own.
        ...(!templateIdOf(item) && { orderAmendmentFileType: "order_amendment" }),
      }),
    });
  };

  const remind = async (signer: RowSigner) => {
    if (!companyId || !signer.signatureId) return;
    setRemindingKey(signer.key);
    const sendReminder = createHandleRepeatEmail(
      companyId,
      () => toast.success(t("files_reminder_sent")),
      () => toast.error(t("files_reminder_failed"))
    );
    await sendReminder(signer.signatureId);
    setRemindingKey(null);
  };

  const view = () => {
    openPdfPreview(dispatch, item.id, companyId);
  };

  // "⋯" menu, top to bottom: open the page, edit, the row's actions that did
  // not get the primary button (in ROW_ACTIONS menu order), then moving the
  // document in the document tree.
  const menuItems: FileRowMenuItem[] = [];
  if (documentPageUrl) {
    menuItems.push({
      key: "open_page",
      label: t("files_open_document_page"),
      onClick: () => navigate(documentPageUrl),
    });
  }
  menuItems.push({ key: "edit", label: t("edit"), onClick: edit, disabled: editMode.disabled });
  for (const { action, labelKey } of getMenuActions(actions, primary)) {
    const isLocked = action === "edit_sign_request" && isEditRequestLocked(item);
    menuItems.push({
      key: action,
      label: t(labelKey),
      onClick: () => runAction(action),
      disabled: isLocked,
      hint: isLocked ? t("edit_sign_request_disabled_partial") : undefined,
    });
  }
  if (ctx.allowParentActions && !isModuleManagedRow(item)) {
    menuItems.push(
      {
        key: "move",
        label: t("move_to_another_document"),
        onClick: () => ctx.openMoveModal(item),
      },
      {
        key: "make_separate",
        label: t("make_separate_document"),
        onClick: () => ctx.openMakeSeparateModal(item),
      }
    );
  }

  const status = getStatusPill(item, signers);
  return {
    title: item.name || item.title || "",
    documentPageUrl,
    status,
    isWithoutSigning: !status && isWithoutSigning(item, signers),
    signers,
    number: getRowNumber(item),
    categoryTitle: getCategoryTitle(item),
    related: item.relatedFiles || [],
    isSelectable: Boolean(ctx.onToggleFileSelection) && isSelectableRow(item, isChild),
    isSelected: Boolean(ctx.selectedFileIds[item.id]),
    toggleSelected: () => ctx.onToggleFileSelection?.(item),
    primary: primary
      ? { label: t(primary.labelKey), run: () => runAction(primary.action) }
      : null,
    menuItems,
    canView: canViewFile(item),
    view,
    downloadUrl: item.fileId ? `${API_URL}/${companyId}/files/download/${item.id}` : null,
    remind,
    remindingKey,
    signedCopyUpload: actions.includes("upload_signed")
      ? { inputRef: uploadInputRef, onFile: uploadSignedCopy }
      : null,
  };
}
